import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { UiButtonComponent, UiRadioButtonComponent, UIIconComponent } from '@design-system';
import { PORTFOLIO_PRICE_BASIS_OPTIONS } from '../../../../models/portfolio-price-basis.model';
import { PORTFOLIO_PRICE_BASES, PortfolioPriceBasis } from '../../../../models/user-setting.model';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';
import { PortfolioViewStateService } from '../../data-access/portfolio-view-state.service';

/** The native `name` the three radios share, so picking one clears the others. */
export const PRICE_BASIS_RADIO_NAME = 'portfolio-price-basis';

function isPortfolioPriceBasis(value: string | number): value is PortfolioPriceBasis {
	return PORTFOLIO_PRICE_BASES.includes(value as PortfolioPriceBasis);
}

/**
 * تنظیمات پرتفوی — reached from the header's three-dot menu, and carrying مبنای
 * قیمت: which price ارزش جاری and سود و زیان are measured at.
 *
 * Unlike the sort sheet this one has a ذخیره button, per scenario 4: the choice
 * is not applied as it is tapped. A basis changes every figure on the screen
 * behind the sheet, so the trader gets to look at the three options before the
 * portfolio is revalued under them — and انصراف leaves the stored basis alone.
 */
@Component({
	selector: 'app-portfolio-settings-bottom-sheet',
	imports: [BottomSheetComponent, UiButtonComponent, UiRadioButtonComponent, UIIconComponent],
	templateUrl: './portfolio-settings-bottom-sheet.component.html',
	host: {
		'data-cy': 'portfolio-settings-bottom-sheet'
	}
})
export class PortfolioSettingsBottomSheetComponent {
	private readonly viewState = inject(PortfolioViewStateService);

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');

	readonly priceBasisOptions = PORTFOLIO_PRICE_BASIS_OPTIONS;
	readonly priceBasisRadioName = PRICE_BASIS_RADIO_NAME;

	/** مبنای قیمت as stored — what the sheet opens on, and what انصراف returns to. */
	private readonly storedPriceBasis = this.viewState.priceBasis;

	/**
	 * The selection as the sheet shows it, seeded from the stored setting by
	 * {@link open}. Held apart from the stored value precisely because ذخیره is
	 * what promotes it: until then the portfolio behind the sheet is still on the
	 * old basis.
	 */
	private readonly selected = signal<PortfolioPriceBasis>('auto');

	readonly activePriceBasis = this.selected.asReadonly();

	isSelected(basis: PortfolioPriceBasis): boolean {
		return this.selected() === basis;
	}

	readonly saving = signal(false);
	readonly saveError = signal<string | null>(null);

	/** Nothing to save until the trader has actually picked something else. */
	readonly canSave = computed(() => this.selected() !== this.storedPriceBasis() && !this.saving());

	/** Seed the sheet from the stored basis, then present it. */
	open(): Promise<void> {
		this.selected.set(this.storedPriceBasis());
		this.saveError.set(null);

		return this.sheet()?.open() ?? Promise.resolve();
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/** A value the radio group reports that this build does not ship is ignored. */
	onPriceBasisChange(value: string | number): void {
		if (!isPortfolioPriceBasis(value)) {
			return;
		}

		this.selected.set(value);
		this.saveError.set(null);
	}

	/**
	 * Scenario 4: the choice applies on ذخیره and is persisted before the sheet
	 * closes, so what the trader sees revalued is what the API now holds. A failed
	 * write keeps the sheet open with the message — closing it would report a
	 * change that was never saved.
	 */
	async onSaveClick(): Promise<void> {
		this.saving.set(true);
		const persisted = await this.viewState.setPriceBasis(this.selected());
		this.saving.set(false);

		if (!persisted) {
			this.saveError.set('ذخیره تنظیمات انجام نشد. دوباره تلاش کنید.');
			return;
		}

		await this.close();
	}

	async onCancelClick(): Promise<void> {
		await this.close();
	}

	/** A dismissed sheet discards its edits, the same as انصراف. */
	onSheetClosed(): void {
		this.selected.set(this.storedPriceBasis());
		this.saveError.set(null);
	}
}
